/**
 * Abrechnung Stufe 2 — die Rechnungsmail aus der Abrechnung steht ZUSÄTZLICH unter „Freigaben“
 * (Konzept ~/maestro-uebergabe/konzept-abrechnung.html § 4 Schritt 4). Kein zweiter Versandweg: jede
 * Zeile klappt dieselbe Tor-2-Karte auf wie im Reiter „Abrechnen“ (`MailKarte`, POST …/abrechnung/mail),
 * mit denselben Sperren (Überzahlung entschieden, LexOffice-Abweichung, fremde Beträge, Doppelversand).
 * Nach 48 h ohne Versand trägt die Zeile „über 48 h“; dann gibt es auch eine Aufgabe (sql/572).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../../hooks/useApi";
import { abrechnungPfad } from "../../lib/pfade";
import { Card, ErrorNote, Icon, LoadingRows, StatusPill, useBestaetigung } from "../components";
import { anlassAnzeige, formatDate, money, timeAgo } from "../format";
import { MailKarte } from "./AbrechnenReiter";
import type { AbrAntwort } from "./typen";

export interface OffeneRechnungsmail {
  eventId: string;
  version: number;
  invoiceNumber: string | null;
  amountGrossCents: number | null;
  erstelltAm: string;
  occasion: string | null;
  eventDate: string | null;
  kunde: string | null;
  ueberfaellig: boolean;
}

function Aufgeklappt({ eventId, onGesendet }: { eventId: string; onGesendet: () => void }) {
  const q = useApi<AbrAntwort>(`/api/events/${eventId}/abrechnung`);
  const [fehler, setFehler] = useState<{ text: string; code?: string } | null>(null);
  const { bestaetigen, dialog } = useBestaetigung();
  if (q.loading && !q.data) return <LoadingRows rows={3} />;
  if (q.error) return <ErrorNote message={q.error} onRetry={q.reload} />;
  const st = q.data;
  if (!st?.mail || !st.rechnung) return <p className="px-[18px] py-3 text-[12.5px] text-secondary">Diese Rechnungsmail ist inzwischen gesendet oder nicht mehr offen.</p>;
  return (
    <div className="border-t border-[#F2ECDF] p-3 dark:border-white/[0.06]">
      {dialog}
      {fehler && <div className="mb-3"><ErrorNote message={fehler.text} /></div>}
      <MailKarte stand={st} busy={false} bestaetigen={bestaetigen} setFehler={setFehler} setStand={() => onGesendet()} eventId={eventId} />
    </div>
  );
}

export const OFFENE_RECHNUNGSMAILS_URL = "/api/abrechnung/offene-rechnungsmails";

/** Die Liste lädt die Seite (`useApi(OFFENE_RECHNUNGSMAILS_URL)`), damit sie ihren Leerzustand kennt. */
export function OffeneRechnungsmails({ q }: { q: { data: OffeneRechnungsmail[] | null | undefined; loading: boolean; error: string | null | undefined; reload: () => void } }) {
  const [offen, setOffen] = useState<string | null>(null);
  const zeilen = q.data ?? [];
  if (q.loading && !q.data) return null;
  if (q.error) return <div className="mb-4"><ErrorNote message={q.error} onRetry={q.reload} /></div>;
  if (zeilen.length === 0) return null;
  return (
    <Card className="mb-4 flex flex-col overflow-hidden" data-testid="freigaben-rechnungsmails">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[#F2ECDF] px-[18px] py-3.5 dark:border-white/[0.06]">
        <h3 className="flex items-center gap-2 font-headline-sm text-[15px] font-bold text-on-background dark:text-inverse-on-surface">
          <Icon name="receipt_long" className="text-[18px] text-primary" /> Rechnungen aus der Abrechnung
        </h3>
        <span className="text-[12px] text-secondary dark:text-secondary-fixed-dim">{zeilen.length === 1 ? "1 Rechnung wartet" : `${zeilen.length} Rechnungen warten`} auf die Mail an den Gast</span>
      </div>
      <ul>
        {zeilen.map((z) => {
          const auf = offen === z.eventId;
          return (
            <li key={z.eventId} className="border-b border-[#F6F0E4] last:border-0 dark:border-white/[0.06]" data-testid="freigaben-rechnungsmail">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-[18px] py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13.5px] font-semibold text-on-background dark:text-inverse-on-surface">
                    {z.kunde ?? "Gast"}{z.occasion ? ` · ${anlassAnzeige(z.occasion)}` : ""}{z.eventDate ? ` · ${formatDate(z.eventDate)}` : ""}
                  </p>
                  <p className="text-[12px] text-secondary dark:text-secondary-fixed-dim">
                    Rechnung {z.invoiceNumber ?? "–"} · {money(z.amountGrossCents)}{z.version > 1 ? ` · Version ${z.version} (mit Gutschrift)` : ""} · {timeAgo(z.erstelltAm)} erstellt
                  </p>
                </div>
                {z.ueberfaellig ? <StatusPill meta={{ label: "über 48 h", tone: "neutral", icon: "schedule" }} /> : <StatusPill meta={{ label: "erstellt · nicht versendet", tone: "warn" }} />}
                <Link to={abrechnungPfad(z.eventId)} className="text-[12.5px] font-semibold text-primary hover:underline">Zur Abrechnung</Link>
                <button type="button" aria-expanded={auf} onClick={() => setOffen(auf ? null : z.eventId)} className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-primary hover:underline">
                  {auf ? "Zuklappen" : "Prüfen und freigeben"} <Icon name={auf ? "expand_less" : "expand_more"} className="text-[16px]" />
                </button>
              </div>
              {auf && <Aufgeklappt eventId={z.eventId} onGesendet={() => { setOffen(null); q.reload(); }} />}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
